import { OpenInFull } from "@mui/icons-material";
import clsx from "clsx";
import { FC, MouseEvent, RefObject } from "react";
import { useTranslation } from "react-i18next";

import { FramerTooltip } from "@ryde/controls";

import {
  buildCoursesCalendarCellSelectAllLabel,
  buildCoursesCalendarCellSelectionBadges,
} from "./courses-calendar-cell.utils";

type CoursesCalendarCellFooterVariant = "cell" | "popper";

interface ICoursesCalendarCellFooterProps {
  totalCount: number;
  selectedCount: number;
  isAllSelected: boolean;
  hasSlotSelection: boolean;
  hasOverflow: boolean;
  variant?: CoursesCalendarCellFooterVariant;
  isExpandOpen?: boolean;
  showExpandButton?: boolean;
  tooltipsDisabled: boolean;
  expandAnchorRef: RefObject<HTMLSpanElement>;
  onToggleSelectAll: () => void;
  onToggleExpand: (event: MouseEvent<HTMLButtonElement>) => void;
}

export const CoursesCalendarCellFooter: FC<ICoursesCalendarCellFooterProps> = ({
  totalCount,
  selectedCount,
  isAllSelected,
  hasSlotSelection,
  hasOverflow,
  variant = "cell",
  isExpandOpen = false,
  showExpandButton = true,
  tooltipsDisabled,
  expandAnchorRef,
  onToggleSelectAll,
  onToggleExpand,
}) => {
  const { t } = useTranslation();
  const { selectedPrefix, fromPrefix } =
    buildCoursesCalendarCellSelectionBadges({
      t,
    });

  return (
    <div
      className={clsx("calendar_cell-footer", {
        "cc-cell-expand-popper__footer": variant === "popper",
        "cc-cell-footer--pinned": variant === "cell" && hasSlotSelection,
        "cc-cell-footer--expand-open": variant === "cell" && isExpandOpen,
        "cc-cell-footer--always-visible": variant === "cell" && hasOverflow,
      })}
    >
      <div className="cc-cell-footer-start">
        <button
          type="button"
          className={clsx("cc-cell-select-link", {
            ["cc-cell-select-link--deselect"]: isAllSelected,
          })}
          onClick={onToggleSelectAll}
        >
          {buildCoursesCalendarCellSelectAllLabel({
            t,
            isAllSelected,
            totalCount,
          })}
        </button>
      </div>
      <div className="cc-cell-footer-end">
        {hasSlotSelection && (
          <>
            <span className="cc-cell-selection-stat">
              <span className="cc-cell-selection-label">{selectedPrefix}</span>
              <span
                className="cc-cell-selection-badge cc-cell-selection-badge--selected"
                data-e2e="courses-calendar-cell-selected-count"
              >
                {selectedCount}
              </span>
            </span>
            <span className="cc-cell-selection-stat">
              <span className="cc-cell-selection-label">{fromPrefix}</span>
              <span
                className="cc-cell-selection-badge cc-cell-selection-badge--from"
                data-e2e="courses-calendar-cell-total-count"
              >
                {totalCount}
              </span>
            </span>
          </>
        )}
        {!hasSlotSelection && (
          <span
            className="cc-cell-selection-badge cc-cell-selection-badge--total"
            data-e2e="courses-calendar-cell-total-count"
          >
            {totalCount}
          </span>
        )}
        {showExpandButton && hasOverflow && (
          <span ref={expandAnchorRef} className="cc-cell-expand-anchor">
            <FramerTooltip
              content={t("course.calendar.expandCellTooltip")}
              tooltipDisabled={tooltipsDisabled && !isExpandOpen}
            >
              <button
                type="button"
                className="cc-cell-expand-button"
                data-e2e="courses-calendar-cell-expand-button"
                aria-label={t("course.calendar.expandCellTooltip")}
                onMouseDown={event => event.stopPropagation()}
                onClick={onToggleExpand}
              >
                <OpenInFull sx={{ fontSize: 14 }} />
              </button>
            </FramerTooltip>
          </span>
        )}
      </div>
    </div>
  );
};
